/**
 * metaLeadGen/useFilters.ts
 *
 * Filter state hook for the Meta Lead Generation tab.
 *
 * Features:
 * - All 10 filter dimensions (date range + 9 multi-select)
 * - Cascading: adset options reset when campaign changes; ad options reset when adset changes
 * - Persistent via localStorage (key: metaLeadGen-filters-v1)
 * - Computed filter object ready for service layer calls
 * - Dynamic options via getMetaLeadGenFilterOptions()
 */

import { useState, useMemo, useCallback } from "react";
import type { MetaLeadGenRow, MetaLeadGenFilters, FilterOptions } from "./types";
import { getMetaLeadGenFilterOptions } from "./service";

// ─────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────
export interface MetaFiltersState {
  dateRange: { start: string; end: string };
  campaignIds: string[];
  adsetIds: string[];
  adIds: string[];
  ages: string[];
  genders: string[];
  devices: string[];
  platforms: string[];
  creativeTypes: string[];
  countries: string[];
}

export type ActiveFilters = MetaLeadGenFilters & { dateRange: { start: string; end: string } };

// ─────────────────────────────────────────────
// DEFAULTS
// ─────────────────────────────────────────────
function buildLast30DaysRange(): { start: string; end: string } {
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 29);
  return { start: fmt(start), end: fmt(end) };
}

export const DEFAULT_DATE_RANGE = buildLast30DaysRange();

const DEFAULT_STATE: MetaFiltersState = {
  dateRange: DEFAULT_DATE_RANGE,
  campaignIds: [],
  adsetIds: [],
  adIds: [],
  ages: [],
  genders: [],
  devices: [],
  platforms: [],
  creativeTypes: [],
  countries: [],
};

// ─────────────────────────────────────────────
// STORAGE
// ─────────────────────────────────────────────
const STORAGE_KEY = "metaLeadGen-filters-v1";

function loadFromStorage(): MetaFiltersState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_STATE;
    return { ...DEFAULT_STATE, ...JSON.parse(raw) };
  } catch {
    return DEFAULT_STATE;
  }
}

function saveToStorage(state: MetaFiltersState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ignore storage errors
  }
}

// ─────────────────────────────────────────────
// HOOK
// ─────────────────────────────────────────────
export function useMetaLeadGenFilters(liveRows?: MetaLeadGenRow[]) {
  const [state, setState] = useState<MetaFiltersState>(loadFromStorage);

  // ── All global options (no filters applied — full universe) ────────────────
  const allOptions: FilterOptions = useMemo(
    () => getMetaLeadGenFilterOptions({}, liveRows),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [liveRows]
  );

  // ── Cascaded adset options — scoped to selected campaigns ──────────────────
  const adsetOptions = useMemo(() => {
    if (!state.campaignIds.length) return allOptions.adsets;
    return getMetaLeadGenFilterOptions({ campaignIds: state.campaignIds }, liveRows).adsets;
  }, [state.campaignIds, allOptions.adsets, liveRows]);

  // ── Cascaded ad options — scoped to selected campaigns + adsets ────────────
  const adOptions = useMemo(() => {
    const f: MetaLeadGenFilters = {};
    if (state.campaignIds.length) f.campaignIds = state.campaignIds;
    if (state.adsetIds.length) f.adsetIds = state.adsetIds;
    return getMetaLeadGenFilterOptions(f, liveRows).ads;
  }, [state.campaignIds, state.adsetIds, allOptions.ads, liveRows]);

  // ── Computed filter object for service calls ───────────────────────────────
  const filters = useMemo((): ActiveFilters => ({
    dateRange: state.dateRange,
    ...(state.campaignIds.length ? { campaignIds: state.campaignIds } : {}),
    ...(state.adsetIds.length ? { adsetIds: state.adsetIds } : {}),
    ...(state.adIds.length ? { adIds: state.adIds } : {}),
    ...(state.ages.length ? { ages: state.ages } : {}),
    ...(state.genders.length ? { genders: state.genders } : {}),
    ...(state.devices.length ? { devices: state.devices } : {}),
    ...(state.platforms.length ? { platforms: state.platforms } : {}),
    ...(state.creativeTypes.length ? { creativeTypes: state.creativeTypes } : {}),
    ...(state.countries.length ? { countries: state.countries } : {}),
  }), [state]);

  // ── Update helper — persists to storage ────────────────────────────────────
  const update = useCallback((updates: Partial<MetaFiltersState>) => {
    setState(prev => {
      const next = { ...prev, ...updates };
      saveToStorage(next);
      return next;
    });
  }, []);

  // ── Cascading setters ──────────────────────────────────────────────────────
  const setCampaignIds = useCallback((ids: string[]) => {
    // Changing campaign resets adset + ad selections
    update({ campaignIds: ids, adsetIds: [], adIds: [] });
  }, [update]);

  const setAdsetIds = useCallback((ids: string[]) => {
    // Changing adset resets ad selection
    update({ adsetIds: ids, adIds: [] });
  }, [update]);

  // ── Reset — restores default date range, clears all dimension filters ──────
  const reset = useCallback(() => {
    setState(DEFAULT_STATE);
    saveToStorage(DEFAULT_STATE);
  }, []);

  // ── Whether any filter differs from defaults ───────────────────────────────
  const hasActiveFilters =
    state.dateRange.start !== DEFAULT_DATE_RANGE.start ||
    state.dateRange.end !== DEFAULT_DATE_RANGE.end ||
    state.campaignIds.length > 0 ||
    state.adsetIds.length > 0 ||
    state.adIds.length > 0 ||
    state.ages.length > 0 ||
    state.genders.length > 0 ||
    state.devices.length > 0 ||
    state.platforms.length > 0 ||
    state.creativeTypes.length > 0 ||
    state.countries.length > 0;

  return {
    state,
    filters,
    allOptions,
    adsetOptions,
    adOptions,
    update,
    setCampaignIds,
    setAdsetIds,
    reset,
    hasActiveFilters,
  };
}
